import {
  Balance,
  ColumnaBalance,
  OperandosColumnaBalance,
  ElementoLogistico,
  EnumTipoOperacion,
  EnumTipoColumna,
  EnumOperando,
  EnumZonaBalance,
  RecuperarBalanceModelResponse,
  EnumLgEnvio,
  OperacionCalculo
} from '../../api'
import {
  BalanceColumnModel,
  BalanceOperandModel,
  LogisticElementModel,
  BalanceOperationCalculationModel,
  BalanceModel
} from './balances.model'
import { DataGridColumn, DataType } from '@globaleris/ui.library'
import { getDataGridColumnParent } from './balances.services'
import {
  enumKeyToValue,
  enumValueToKey,
  enumEnumToValueString,
  enumEnumToValueNumber
} from '../../helpers'
import moment from 'moment'
import { getActiveCompany, getActiveBalanceZone } from '../../redux'
import { GuardarBalanceModel } from '../../api/Pioneer/endpoints/requests/guardarBalance.request'

export const mapBalanceListFromApi = (items: Balance[]): BalanceModel[] => {
  return items && items.map(mapBalanceFromApi)
}

export const mapBalanceFromApi = (item: Balance): BalanceModel =>
  item && {
    id: item.id,
    description: item.descripcion,
    tipo: item.tipoBalance,
    zone: item.zonaBalance,
    columnsConfiguration: mapBalanceColumnListFromApi(item.listaColumnas.sort(c => c.orden)),
    logisticElements: mapLogisticElementListFromApi(item.elementosLogisticos)
  }

export const mapBalanceColumnListFromApi = (items: ColumnaBalance[]): BalanceColumnModel[] => {
  return items && items.map(mapBalanceColumnFromApi)
}

export const mapBalanceColumnFromApi = (item: ColumnaBalance): BalanceColumnModel => {
  const allowHighlightingOperationTypes = [
    EnumTipoOperacion.Regasificacion,
    EnumTipoOperacion.EntradaRtPCPR,
    EnumTipoOperacion.EntradaRtPCI,
    EnumTipoOperacion.EntradaRtPCY,
    EnumTipoOperacion.SalidaRtPCI,
    EnumTipoOperacion.DemandaLLDD,
    EnumTipoOperacion.Compra,
    EnumTipoOperacion.Venta,
    EnumTipoOperacion.DemandaLLDDME,
    EnumTipoOperacion.Licuefaccion,
    EnumTipoOperacion.Inyección,
    EnumTipoOperacion.Extracción
  ]
  return (
    item && {
      description: item.descripcion,
      visible: item.visible,
      editable: !item.noEditable,
      send: enumKeyToValue(EnumLgEnvio, item.lgEnvio) === EnumLgEnvio.Si ? true : false,
      id: item.id,
      type: enumKeyToValue(EnumTipoColumna, item.tipoColumna),
      order: item.orden,
      parentColumnId: item.idColumnaPadre,
      operationType: enumKeyToValue(EnumTipoOperacion, item.tipoOperacion),
      logisticElementId: item.idElementosLogistico,
      logisticElementType: item.tipoElementosLogistico,
      logisticElementSubtype: item.subtipoElementosLogistico,
      logisticElementDescriptiom: item.desElementosLogistico,
      zone: enumKeyToValue(EnumZonaBalance, item.zonaBalance),
      relatedColumns: mapBalanceOperandListFromApi(item.listaColumnas),

      // Configure which columns should allow cell highlighting
      // For aggregated columns (without operation type), default to true
      // For regular columns, check if operation type is in the allowed list
      allowHighlighting:
        item.tipoOperacion === null || item.tipoOperacion === undefined
          ? true
          : allowHighlightingOperationTypes.includes(
              enumEnumToValueNumber(EnumTipoOperacion, item.tipoOperacion)!
            )
      // Only highlight specific operation types
      // allowHighlighting: ['Desbalance', 'Consumo', 'Entrada'].includes(item.tipoOperacion)

      // Don't highlight by column ID
      // allowHighlighting: ![27, 28, 29].includes(item.id)

      // Only highlight editable columns
      // allowHighlighting: !item.noEditable

      // Never highlight any columns
      // allowHighlighting: false
    }
  )
}

export const mapBalanceOperandListFromApi = (
  items: OperandosColumnaBalance[]
): BalanceOperandModel[] => {
  return items && items.map(mapBalanceOperandFromApi)
}

export const mapBalanceOperandFromApi = (item: OperandosColumnaBalance): BalanceOperandModel =>
  item && {
    id: item.idOperandoBalance,
    type: enumKeyToValue(EnumOperando, item.tipoOperando),
    relatedColumnId: item.columnaRelacionada
  }

export const mapBalanceDataGridColumns = (columns: Array<BalanceColumnModel>): DataGridColumn[] => {
  let gridColumns: DataGridColumn[] = []
  gridColumns.push({
    label: 'Fecha',
    editable: false,
    name: 'date',
    type: DataType.DATE
  })

  columns.map(column =>
    gridColumns.push({
      label: column.description,
      display: column.visible,
      editable: column.editable,
      name: column.id.toString(),
      subcolumns: getDataGridColumnParent(columns, column.id)
    })
  )

  return gridColumns
}

export const mapLogisticElementListFromApi = (
  items: ElementoLogistico[]
): LogisticElementModel[] => {
  return items && items.map(mapLogisticElementFromApi)
}

export const mapLogisticElementFromApi = (item: ElementoLogistico): LogisticElementModel =>
  item && {
    id: item.id,
    zone: item.zonaBalance
  }

export const mapBalanceOperationCalculationListFromApi = (
  items: RecuperarBalanceModelResponse[]
): BalanceOperationCalculationModel[] => {
  return items && items.map(mapBalanceOperationCalculationFromApi)
}

export const mapBalanceOperationCalculationFromApi = (
  item: RecuperarBalanceModelResponse
): BalanceOperationCalculationModel =>
  item && {
    balanceColumnId: item.columna,
    date: item.fechaOperacion,
    quantity: item.cantidad,
    type: enumKeyToValue(EnumTipoOperacion, item.tipoOperacion),
    send: enumKeyToValue(EnumLgEnvio, item.lgEnvio) === EnumLgEnvio.Si ? true : false,
    logisticElementId: item.idElemLog,
    zone: enumKeyToValue(EnumZonaBalance, item.zonaBalance),
    modified: false,
    value: item.valor,
    typeValue: item.tipoValor,
    areQuantitiesDifferent: item.areQuantitiesDifferent || false,
    quantityTSO: item.cantidadTSO
  }

export const mapOperacionCalculoListFromApi = (
  items: OperacionCalculo[]
): BalanceOperationCalculationModel[] => {
  return items && items.map(mapOperacionCalculoFromApi)
}

export const mapOperacionCalculoFromApi = (
  item: OperacionCalculo
): BalanceOperationCalculationModel =>
  item && {
    date: moment(item.fechaOperacion, 'YYYY-MM-DDT00:00:00.000[Z]').toDate(),
    quantity: item.cantidad,
    type: enumValueToKey(EnumTipoOperacion, item.tipoOperacion),
    zone: enumValueToKey(EnumZonaBalance, item.zonaBalance),
    logisticElementId: item.idElemLogistico,
    companyId: item.idEmpresa,
    modified: false
  }

export const mapBalanceOperationCalculationListToApi = (
  items: BalanceOperationCalculationModel[]
): OperacionCalculo[] => {
  return items && items.map(mapBalanceOperationCalculationToApi)
}

export const mapBalanceOperationCalculationToApi = (
  item: BalanceOperationCalculationModel
): OperacionCalculo =>
  item && {
    fechaOperacion: moment(item.date).format('YYYY-MM-DD'),
    //fechaOperacion: item.date,
    cantidad: item.quantity,
    tipoOperacion: enumKeyToValue(EnumTipoOperacion, item.type),
    idEmpresa: getActiveCompany()?.id,
    zonaBalance: enumKeyToValue(EnumZonaBalance, getActiveBalanceZone()?.id),
    idElemLogistico: item.logisticElementId ?? 0,
    tipoValor: item?.typeValue,
    valor: item?.value
  }

export const mapGuardarBalanceRequestToApi = (
  items: Array<BalanceOperationCalculationModel>
): GuardarBalanceModel[] => {
  return items && items.map(mapGuardarBalanceRequestItemToApi)
}

export const mapGuardarBalanceRequestItemToApi = (
  item: BalanceOperationCalculationModel
): GuardarBalanceModel => {
  return (
    item && {
      columna: item.balanceColumnId!,
      cantidadModificada: item.quantity !== undefined && item.quantity !== null ? item.quantity : 0,
      fechaOperacion: moment(item.date).format('YYYY-MM-DD'),
      idElemenLog: item.logisticElementId!,
      idEmpresa: getActiveCompany()?.id!,
      tipoOperacion: enumKeyToValue(EnumTipoOperacion, item.type),
      zonaBalance: enumKeyToValue(EnumZonaBalance, item.zone)
    }
  )
}

export const formatKwh = (value: number): string => {
    return Math.round(value).toLocaleString('es-ES')
  }